import { QUICK_LINKS, DEFAULT_QUICK_LINKS, getQuickLink } from './quick-links';

/**
 * 빠른 실행 — 바로가기 켜기 · 순서 (2026-09-16 · 대시보드 시안 v2 V4 · 순수 함수).
 *
 * 저장 모양은 **그대로** `settings.quickLinks: string[]` 다 — 배열 순서가 곧 위젯에 놓이는 순서다
 * (위젯은 이미 그 순서로 그린다). 옛 설정 창은 고를 때마다 카탈로그 순서로 되돌렸고, v2 창은 사용자가 정한 순서를 둔다.
 *
 * ★★★ **메뉴가 꺼진 바로가기는 목록에 남되 막힌다**(시안 「관리자가 할일 메뉴를 꺼서 고를 수 없습니다」).
 *     이미 골라 둔 것이면 저장값을 지우지 않는다 — 메뉴가 돌아오면 그 자리에 선다(V1 규칙 D2). 위젯은 지금처럼 그리지 않는다.
 * ★★ 모르는 key(판이 오간 저장값)는 버리지 않고 뒤에 붙여 둔다(M3).
 * ★ 순서는 **켠 것끼리만** 뜻이 있다 — 목록은 켠 것(저장 순서) 다음 안 켠 것(카탈로그 순서)으로 선다.
 */

/** 막힘 문구에 쓰는 메뉴 이름 — 위젯의 `useMenuGate` 이름과 같은 말. */
export const QUICK_LINK_MENU_LABEL: Readonly<Record<string, string>> = {
  approval: '전자결재', attendance: '근태', reservation: '예약', mail: '메일', workspace: '워크스페이스',
  calendar: '캘린더', board: '게시판', contacts: '명함첩', chat: '대화', task: '할일',
};

/** 줄 설명 — 주소가 아니라 창을 여는 것처럼 눌렀을 때를 짐작하기 어려운 것만. */
export const QUICK_LINK_DESC: Readonly<Record<string, string>> = {
  leave: '근태 신청 창을 엽니다',
};

export interface QuickLinkRow {
  key: string;
  label: string;
  on: boolean;
  lockedReason: string | null;
}

const KNOWN = new Set(QUICK_LINKS.map((d) => d.key));

/** 켠 바로가기(알려진 key · 중복 없이 · 저장 순서). 저장값이 없으면 기본 여덟. */
export function pickedQuickLinks(stored: readonly string[] | undefined): string[] {
  return [...new Set(stored ?? DEFAULT_QUICK_LINKS)].filter((k) => KNOWN.has(k));
}

function unknownOf(stored: readonly string[] | undefined): string[] {
  return (stored ?? []).filter((k) => !KNOWN.has(k));
}

export function quickLinkRows(
  stored: readonly string[] | undefined,
  menuEnabled: (menuKey: string) => boolean,
): QuickLinkRow[] {
  const picked = pickedQuickLinks(stored);
  const on = new Set(picked);
  const order = [...picked, ...QUICK_LINKS.map((d) => d.key).filter((k) => !on.has(k))];
  return order.map((key) => {
    const d = getQuickLink(key)!;
    const off = d.menuKey !== undefined && !menuEnabled(d.menuKey);
    return {
      key,
      label: d.label,
      on: on.has(key),
      lockedReason: off ? `관리자가 ${QUICK_LINK_MENU_LABEL[d.menuKey!] ?? d.menuKey} 메뉴를 꺼서 고를 수 없습니다` : null,
    };
  });
}

/** 켜면 켠 것들의 맨 뒤에 붙고, 끄면 빠진다. */
export function toggleQuickLink(stored: readonly string[] | undefined, key: string, on: boolean): string[] {
  const picked = pickedQuickLinks(stored);
  const next = on ? (picked.includes(key) ? picked : [...picked, key]) : picked.filter((k) => k !== key);
  return [...next, ...unknownOf(stored)];
}

/**
 * 목록 자리 from → to (끌기 · 키보드가 같은 함수). 켠 것의 순서만 저장된다 —
 * 안 켠 줄을 옮겨도 저장값은 그대로이고, 그 줄은 다시 그릴 때 안 켠 것들 사이 제자리로 간다.
 */
export function moveQuickLink(stored: readonly string[] | undefined, from: number, to: number): string[] | null {
  const rows = quickLinkRows(stored, () => true).map((r) => r.key);
  if (from === to || from < 0 || to < 0 || from >= rows.length || to >= rows.length) return null;
  const next = [...rows];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved!);
  const on = new Set(pickedQuickLinks(stored));
  return [...next.filter((k) => on.has(k)), ...unknownOf(stored)];
}

/** 기본 여덟과 같은가(순서까지) — 「바뀜」 셈. */
export function isDefaultQuickLinks(stored: readonly string[] | undefined): boolean {
  return stored === undefined || stored.join(',') === DEFAULT_QUICK_LINKS.join(',');
}
